import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import type { IconGeneratorStatus } from '../hooks/useIconGenerator';

interface IconGeneratorFormProps {
  status: IconGeneratorStatus;
  onGenerate: (name: string, description: string, useDefaultStyle: boolean, style?: string) => void;
  onSave: (options?: { fileName?: string; attachToArcheId?: string }) => void;
  onReset: () => void;
  /**
   * When set, renders a dedicated "Use as my arche icon" button that
   * promotes the generated image to the arche's canonical display icon.
   * Typically passed from the arche-builder when the user opens the
   * generator from the IconPicker.
   */
  attachToArcheId?: string;
  /**
   * Set by the hook once the save+attach round-trip completes — used to
   * decide when to bounce the user back to `returnTo`.
   */
  attachedIconKey?: string | null;
  /**
   * Optional same-origin path to navigate to once the icon has been
   * attached. The page guards against off-origin values before passing
   * the prop in.
   */
  returnTo?: string;
}

export function IconGeneratorForm({
  status,
  onGenerate,
  onSave,
  onReset,
  attachToArcheId,
  attachedIconKey,
  returnTo,
}: IconGeneratorFormProps) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [useDefaultStyle, setUseDefaultStyle] = useState(true);
  const [style, setStyle] = useState('');
  // Set by handleSaveAndAttach to opt this round-trip into the auto-return
  // navigation. Without it, a user clicking "Save to Files" would also be
  // bounced back to the builder, which isn't what they asked for.
  const attachIntentRef = useRef(false);

  // When the save+attach succeeds (status === 'saved' AND attachedIconKey
  // populated AND we initiated an attach), bounce back to the builder so
  // the IconPicker re-renders with the freshly-attached icon.
  useEffect(() => {
    if (status === 'saved' && attachedIconKey && attachIntentRef.current && returnTo) {
      attachIntentRef.current = false;
      navigate(returnTo);
    }
  }, [status, attachedIconKey, returnTo, navigate]);

  const isGenerating = status === 'generating';
  const isSaving = status === 'saving';
  const canGenerate =
    name.trim().length > 0 && description.trim().length > 0 && !isGenerating && !isSaving;
  const canSave = status === 'generated';

  const handleGenerate = () => {
    if (!canGenerate) return;
    onGenerate(name.trim(), description.trim(), useDefaultStyle, style.trim() || undefined);
  };

  const safeFileName = (() => {
    const slug = name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return slug ? `${slug}-icon` : 'icon';
  })();

  const handleSave = () => {
    if (!canSave) return;
    onSave({ fileName: safeFileName });
  };

  const handleSaveAndAttach = () => {
    if (!canSave || !attachToArcheId) return;
    attachIntentRef.current = true;
    onSave({ fileName: safeFileName, attachToArcheId });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="icon-name">Name</Label>
        <Input
          id="icon-name"
          placeholder="e.g., Chat, Calendar, Marketplace"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          disabled={isGenerating}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="icon-description">Description</Label>
        <Textarea
          id="icon-description"
          placeholder="e.g., Real-time messaging and channels for teams"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          rows={3}
          disabled={isGenerating}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="use-default-style"
          checked={useDefaultStyle}
          onCheckedChange={(checked) => setUseDefaultStyle(checked === true)}
          disabled={isGenerating}
        />
        <Label htmlFor="use-default-style" className="text-sm font-normal cursor-pointer">
          Use default Fias style (aurora gradient)
        </Label>
      </div>

      {!useDefaultStyle && (
        <div className="space-y-2">
          <Label htmlFor="icon-style">Custom Style</Label>
          <Textarea
            id="icon-style"
            placeholder="e.g., Warm sunset orange gradient with golden accents"
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            maxLength={500}
            rows={2}
            disabled={isGenerating}
          />
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={handleGenerate} disabled={!canGenerate}>
          {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isGenerating ? 'Generating...' : 'Generate'}
        </Button>

        {canSave && (
          <Button onClick={handleSave} variant="secondary">
            Save to Files
          </Button>
        )}

        {canSave && attachToArcheId && (
          <Button onClick={handleSaveAndAttach} variant="default">
            Use as my arche icon
          </Button>
        )}

        {isSaving && (
          <Button disabled variant="secondary">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </Button>
        )}

        {(status === 'generated' || status === 'saved' || status === 'error') && (
          <Button onClick={onReset} variant="outline">
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
